import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as TriggerIcon,
  FilterAlt as ConditionIcon,
  Settings as ActionIcon,
  CallSplit as RouterIcon,
  Loop as LoopIcon,
  Schedule as DelayIcon,
  Task as TaskIcon,
  Person as UserIcon,
  Notifications as NotificationIcon,
  Email as EmailIcon,
  Api as ApiIcon,
  Storage as DataIcon,
  AccountTree as WorkflowIcon,
  Assignment as ProjectIcon,
  Flag as StatusIcon,
  Label as FieldIcon,
  DateRange as DateIcon,
  Timer as TimeIcon,
  AttachMoney as BudgetIcon
} from '@mui/icons-material';

// Node categories with their items
const nodeCategories = [
  {
    name: 'Triggers',
    icon: <TriggerIcon />,
    color: '#4caf50',
    items: [
      { type: 'trigger', subtype: 'task_created', label: 'Task Created', icon: <TaskIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'task_updated', label: 'Task Updated', icon: <TaskIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'task_status_changed', label: 'Status Changed', icon: <StatusIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'task_assigned', label: 'Task Assigned', icon: <UserIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'due_date_approaching', label: 'Due Date Soon', icon: <DateIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'field_changed', label: 'Field Changed', icon: <FieldIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'time_based', label: 'Scheduled', icon: <TimeIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'manual', label: 'Manual Trigger', icon: <PlayArrowIcon fontSize="small" /> },
      { type: 'trigger', subtype: 'webhook', label: 'Webhook', icon: <ApiIcon fontSize="small" /> }
    ]
  },
  {
    name: 'Conditions',
    icon: <ConditionIcon />,
    color: '#ff9800',
    items: [
      { type: 'condition', subtype: 'field_equals', label: 'Field Equals', icon: <FieldIcon fontSize="small" /> },
      { type: 'condition', subtype: 'field_contains', label: 'Field Contains', icon: <FieldIcon fontSize="small" /> },
      { type: 'condition', subtype: 'status_is', label: 'Status Is', icon: <StatusIcon fontSize="small" /> },
      { type: 'condition', subtype: 'assignee_is', label: 'Assignee Is', icon: <UserIcon fontSize="small" /> },
      { type: 'condition', subtype: 'date_before', label: 'Date Before', icon: <DateIcon fontSize="small" /> },
      { type: 'condition', subtype: 'date_after', label: 'Date After', icon: <DateIcon fontSize="small" /> },
      { type: 'condition', subtype: 'priority_is', label: 'Priority Is', icon: <FlagIcon fontSize="small" /> },
      { type: 'condition', subtype: 'has_attachment', label: 'Has Attachment', icon: <AttachFileIcon fontSize="small" /> }
    ]
  },
  {
    name: 'Actions',
    icon: <ActionIcon />,
    color: '#2196f3',
    items: [
      { type: 'action', subtype: 'update_field', label: 'Update Field', icon: <FieldIcon fontSize="small" /> },
      { type: 'action', subtype: 'change_status', label: 'Change Status', icon: <StatusIcon fontSize="small" /> },
      { type: 'action', subtype: 'assign_to', label: 'Assign To', icon: <UserIcon fontSize="small" /> },
      { type: 'action', subtype: 'add_collaborator', label: 'Add Collaborator', icon: <UserIcon fontSize="small" /> },
      { type: 'action', subtype: 'create_task', label: 'Create Task', icon: <TaskIcon fontSize="small" /> },
      { type: 'action', subtype: 'create_subtask', label: 'Create Subtask', icon: <TaskIcon fontSize="small" /> },
      { type: 'action', subtype: 'move_to_section', label: 'Move to Section', icon: <FolderIcon fontSize="small" /> },
      { type: 'action', subtype: 'add_comment', label: 'Add Comment', icon: <CommentIcon fontSize="small" /> },
      { type: 'action', subtype: 'send_notification', label: 'Send Notification', icon: <NotificationIcon fontSize="small" /> },
      { type: 'action', subtype: 'send_email', label: 'Send Email', icon: <EmailIcon fontSize="small" /> },
      { type: 'action', subtype: 'webhook', label: 'Call Webhook', icon: <ApiIcon fontSize="small" /> },
      { type: 'action', subtype: 'update_budget', label: 'Update Budget', icon: <BudgetIcon fontSize="small" /> }
    ]
  },
  {
    name: 'Flow Control',
    icon: <AccountTreeIcon />,
    color: '#9c27b0',
    items: [
      { type: 'router', subtype: 'router', label: 'Router (If/Then)', icon: <RouterIcon fontSize="small" /> },
      { type: 'loop', subtype: 'for_each', label: 'For Each', icon: <LoopIcon fontSize="small" /> },
      { type: 'loop', subtype: 'while', label: 'While Loop', icon: <LoopIcon fontSize="small" /> },
      { type: 'delay', subtype: 'wait', label: 'Wait/Delay', icon: <DelayIcon fontSize="small" /> }
    ]
  }
];

// Additional imports for missing icons
import {
  PlayArrow as PlayArrowIcon,
  Flag as FlagIcon,
  AttachFile as AttachFileIcon,
  Folder as FolderIcon,
  Comment as CommentIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';

const WorkflowSidebar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    'Triggers': true,
    'Conditions': true,
    'Actions': true,
    'Flow Control': true
  });

  // Handle drag start
  const onDragStart = (event, nodeType, nodeData) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('nodeData', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter items based on search
  const filterItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return (
    <Paper
      sx={{
        width: 280,
        height: '100%',
        borderRadius: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Workflow Components
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search components..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Categories */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {nodeCategories.map((category) => {
          const filteredItems = filterItems(category.items);
          if (searchTerm && filteredItems.length === 0) return null;

          return (
            <Accordion
              key={category.name}
              expanded={expandedCategories[category.name]}
              onChange={() => toggleCategory(category.name)}
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                backgroundColor: 'transparent'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 48,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 1
                  }
                }}
              >
                <Box sx={{ color: category.color }}>
                  {category.icon}
                </Box>
                <Typography variant="subtitle2">
                  {category.name}
                </Typography>
                <Chip 
                  label={filteredItems.length} 
                  size="small" 
                  sx={{ ml: 'auto', mr: 1 }}
                />
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <List dense sx={{ py: 0 }}>
                  {filteredItems.map((item) => (
                    <ListItem
                      key={`${item.type}-${item.subtype}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, item.type, item)}
                      sx={{
                        cursor: 'grab',
                        borderRadius: 1,
                        mb: 0.5,
                        mx: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        },
                        '&:active': {
                          cursor: 'grabbing'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, color: category.color }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.label}
                        primaryTypographyProps={{
                          variant: 'body2'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Footer Tips */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Drag components to the canvas to add them to your workflow
        </Typography>
      </Box>
    </Paper>
  );
};

export default WorkflowSidebar;